import { useEffect, useState } from "react"
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import { Pagination } from "./Pagination"
import Modal from "@/components/Modal"
import FormHeader from "@/components/FormHeader"
import { MdSync } from "react-icons/md"
import { Loader2 } from "lucide-react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import toast from "react-hot-toast"
import api from "@/lib/axios"
import { AxiosError } from "axios"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { NilaiRapor, PaginationData, Periode } from "@/types"
import { getListPeriode } from "@/services/getData"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useDebounce } from "@/hooks/useDebounce"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  paginationData: PaginationData<NilaiRapor>
}

const postData = async (id_sys_periode: string) => {
  const response = await api.post("/sinkron/nilai-rapor-snbp/sync", {
    id_sys_periode,
  })
  return response.data
}

export function DataTable<TData, TValue>({
  columns,
  data,
  paginationData,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})

  const [showSync, setShowSync] = useState(false)
  const [form, setForm] = useState({
    id_sys_periode: "",
  })

  const [searchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || ""
  )
  const debouncedQuery = useDebounce(searchQuery, 1000)
  const navigate = useNavigate()
  const location = useLocation()

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const { data: dataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: postData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listNilaiRaporSNBP"] })
      setShowSync(false)
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSyncMataPelajaran = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    mutate(form.id_sys_periode)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)

    if (debouncedQuery) {
      if (params.get("search") !== debouncedQuery) {
        params.delete("page")
      }

      if (
        params.get("search") !== debouncedQuery ||
        params.get("page") !== "1"
      ) {
        params.set("search", debouncedQuery)
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    } else {
      if (params.has("search") || params.get("page") !== "1") {
        params.delete("search")
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        })
      }
    }
  }, [debouncedQuery, navigate, location.pathname, location.search])

  return (
    <div>
      <div className="flex items-center justify-between gap-3 py-4">
        <Input
          placeholder="Filter by nama atau nomor pendaftaran..."
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          className="max-w-sm"
        />
        <div className="flex gap-x-3">
          <Button
            className="flex items-center gap-x-3"
            onClick={() => setShowSync(true)}
          >
            Sinkronisasi
            <MdSync />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="border rounded-xl">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <br />
      <div>
        <Pagination table={table} paginationData={paginationData} />
      </div>

      <Modal
        show={showSync}
        onClose={() => setShowSync(false)}
        closeable={!isPending}
      >
        <form onSubmit={handleSyncMataPelajaran}>
          <FormHeader
            icon={<MdSync />}
            title="Sinkronisasi Data Nilai Rapor SNBP"
            description={`Apakah anda yakin akan melakukan sinkronisasi data nilai rapor SNBP?`}
          />
          <div>
            <Label>Periode</Label>
            <Select
              required
              value={form.id_sys_periode}
              onValueChange={(value) => {
                setForm((prev) => ({ ...prev, id_sys_periode: value }))
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={"Pilih Periode"} />
              </SelectTrigger>
              <SelectContent>
                {dataPeriode?.map((periode: Periode) => (
                  <SelectItem
                    key={periode.id_sys_periode}
                    value={`${periode.id_sys_periode}`}
                  >
                    {periode.tahun} - {periode.jalur_jenis?.nama_jalur_jenis} -{" "}
                    Gelombang {periode.gelombang}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-5 flex gap-3">
            <Button
              type="button"
              variant={"outline"}
              disabled={isPending}
              onClick={() => setShowSync(false)}
            >
              Batal
            </Button>
            <Button
              variant={"default"}
              className="flex items-center gap-3"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Processing...
                </>
              ) : (
                "Sinkron"
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
